import React, { useId, useState } from 'react';
import SharedRuleEquivalents from './SharedRuleEquivalents';
import { isOfferFormat } from './OfferAvailabilityDialog';
import { FiChevronDown, FiLock, FiTag, FiUnlock } from 'react-icons/fi';

const RULE_MODES = new Set(['markup_amount', 'markup_percent', 'fixed_price']);
export const MAX_BULK_PROMOTION_COUNT = 1000;

const parseNonNegativeNumber = (value, { integer = false } = {}) => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0 || (integer && !Number.isInteger(parsed))) {
    return null;
  }
  return parsed;
};

export const buildBulkPriceListChanges = ({
  applyRule,
  ruleMode,
  ruleValue,
  applyPrice,
  sellingPrice,
  normalizedPriceRuleSelection = false,
}) => {
  if (!applyRule && !applyPrice) {
    return { error: 'Elige una regla o un precio final.', field: 'operations' };
  }
  if (applyRule && applyPrice) {
    return { error: 'Elige solo un tipo de cambio a la vez.', field: 'operations' };
  }

  const changes = {};
  if (applyRule) {
    if (!ruleMode) {
      return { error: 'Elige una regla o la opción Quitar regla.', field: 'ruleMode' };
    }
    if (ruleMode === 'clear') {
      changes[normalizedPriceRuleSelection ? 'priceRule' : 'stockPriceRule'] = null;
    } else if (String(ruleMode).startsWith('complex:')) {
      const complexPriceRuleId = Number(String(ruleMode).slice('complex:'.length));
      if (!Number.isSafeInteger(complexPriceRuleId) || complexPriceRuleId <= 0) {
        return { error: 'Elige una regla compleja válida.', field: 'ruleMode' };
      }
      changes.priceRule = { type: 'complex', complexPriceRuleId };
    } else if (!RULE_MODES.has(ruleMode)) {
      return { error: 'Elige una regla de precio válida.', field: 'ruleMode' };
    } else {
      const ruleNumber = parseNonNegativeNumber(ruleValue, {
        integer: ruleMode !== 'markup_percent',
      });
      if (ruleNumber === null) {
        return {
          error: ruleMode === 'markup_percent'
            ? 'Ingresa un porcentaje válido.'
            : 'Ingresa un valor entero válido para la regla.',
          field: 'ruleValue',
        };
      }
      const simpleRule = { mode: ruleMode, value: ruleNumber };
      if (normalizedPriceRuleSelection) {
        changes.priceRule = { type: 'simple', rule: simpleRule };
      } else {
        changes.stockPriceRule = simpleRule;
      }
    }
  }

  if (applyPrice) {
    const priceNumber = parseNonNegativeNumber(sellingPrice, { integer: true });
    if (priceNumber === null) {
      return { error: 'Ingresa un precio final entero válido.', field: 'sellingPrice' };
    }
    changes.sellingPrice = priceNumber;
  }

  return { changes };
};

const QuickPriceBulkEditor = ({
  filters,
  canEditPricing = true,
  selectedCount = 0,
  selectedVisibleCount = selectedCount,
  visibleCount = 0,
  allVisibleSelected = false,
  saving = false,
  disabled = false,
  selectedPricingBases = [],
  selectedFormats = [],
  offerView = false,
  selectedUnconfiguredCount = 0,
  lockedVisibleCount = 0,
  selectedLockedCount = 0,
  selectedUnlockedCount = Math.max(0, selectedCount - selectedLockedCount),
  complexPriceRules = [],
  normalizedPriceRuleSelection = false,
  lockSaving = false,
  onSelectVisible,
  onClearSelection,
  onCreatePromotions,
  onLockSelected,
  onUnlockSelected,
  onApply,
}) => {
  const generatedId = useId().replace(/:/g, '');
  const [operation, setOperation] = useState('');
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const rulesOnly = offerView || selectedFormats.some(isOfferFormat);
  const selectedOperation = rulesOnly ? 'rule' : operation;
  const applyRule = selectedOperation === 'rule';
  const applyPrice = selectedOperation === 'price';
  const [ruleMode, setRuleMode] = useState('');
  const [ruleValue, setRuleValue] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState('');
  const controlsDisabled = disabled || saving || lockSaving;
  const editingDisabled = controlsDisabled || !selectedCount || selectedLockedCount > 0;
  const errorId = `${generatedId}-error`;
  const selectedBasisSet = new Set(selectedPricingBases);
  const mixedPricingBases = selectedBasisSet.has('unit') && selectedBasisSet.has('kg');
  const monetaryRule = ['markup_amount', 'fixed_price'].includes(ruleMode);
  const complexRuleSelected = String(ruleMode).startsWith('complex:');
  const previewRule = buildBulkPriceListChanges({ applyRule, ruleMode, ruleValue, normalizedPriceRuleSelection: true }).changes?.priceRule;
  const previewFormats = selectedFormats.map((format) => ({ ...format, priceRule: previewRule,
    stockPriceRule: null, analysis: { ...format.analysis, priceRule: undefined } }));
  const hiddenSelectedCount = Math.max(0, selectedCount - selectedVisibleCount);
  const exceedsPromotionLimit = selectedCount > MAX_BULK_PROMOTION_COUNT;

  const submit = async (event) => {
    event.preventDefault();
    if (!canEditPricing) return;
    if (!selectedCount) {
      setError('Selecciona al menos un formato.');
      setErrorField('selection');
      return;
    }
    if (selectedLockedCount > 0) {
      setError('Desbloquea los formatos seleccionados antes de editarlos.');
      setErrorField('selection');
      return;
    }
    const result = buildBulkPriceListChanges({
      applyRule,
      ruleMode,
      ruleValue,
      applyPrice,
      sellingPrice,
      normalizedPriceRuleSelection,
    });
    if (result.error) {
      setError(result.error);
      setErrorField(result.field || 'operations');
      return;
    }
    setError('');
    setErrorField('');
    await onApply?.(result.changes);
  };

  const clearError = () => { setError(''); setErrorField(''); };
  const toggleClassName = (checked) => `flex min-h-8 cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition ${checked
    ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`;
  const inputClassName = 'min-h-9 w-full min-w-0 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50';

  return (
    <form aria-label="Selección y actualización masiva" aria-labelledby={`${generatedId}-title`} onSubmit={submit}
      className="grid min-w-0 gap-3 p-3 lg:grid-cols-[minmax(0,0.44fr)_minmax(0,0.56fr)]">
      <div className="min-w-0 lg:border-r lg:border-slate-200 lg:pr-3">
        {filters}
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 lg:flex-col lg:items-stretch lg:gap-y-2 ${filters ? 'mt-2 border-t border-slate-200 pt-2' : ''}`}>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 lg:border-b lg:border-slate-200 lg:pb-2">
            <span aria-live="polite" className="text-xs font-bold text-blue-600">
              {selectedCount} {selectedCount === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <span className="text-xs text-slate-500">
              de {visibleCount}<span className="sr-only"> {visibleCount === 1 ? 'visible' : 'visibles'}</span>
              {lockedVisibleCount > 0 && <> · {lockedVisibleCount} {lockedVisibleCount === 1 ? 'bloqueado' : 'bloqueados'}</>}
            </span>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-y-1 lg:ml-0">
            <button type="button" disabled={controlsDisabled || visibleCount <= lockedVisibleCount || allVisibleSelected}
              onClick={() => { clearError(); onSelectVisible?.(); }}
              className="min-h-8 pr-2 text-xs font-semibold text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50">
              Seleccionar visibles
            </button>
            <button type="button" aria-label="Quitar selección" disabled={controlsDisabled || !selectedCount}
              onClick={() => { clearError(); onClearSelection?.(); }}
              className="min-h-8 border-l border-slate-200 px-2 text-xs font-semibold text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50">
              Limpiar
            </button>
            {canEditPricing && <details className="relative ml-2 lg:ml-auto">
              <summary className="flex min-h-8 cursor-pointer list-none items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 [&::-webkit-details-marker]:hidden">
                <FiLock aria-hidden="true" className="h-4 w-4" />Bloqueos<FiChevronDown aria-hidden="true" className="h-4 w-4" />
              </summary>
              <div className="absolute right-0 top-full z-30 mt-2 w-72 rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
                <button type="button" disabled={controlsDisabled || !selectedCount || !selectedUnlockedCount}
                  title="Bloquea el precio final y la regla solo de los formatos seleccionados."
                  onClick={(event) => { event.currentTarget.closest('details').removeAttribute('open'); onLockSelected?.(); }}
                  className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <FiLock aria-hidden="true" />{lockSaving ? 'Actualizando…' : `Bloquear seleccionados (${selectedUnlockedCount})`}
                </button>
                <button type="button" disabled={controlsDisabled || !selectedCount || !selectedLockedCount}
                  title="Desbloquea el precio final y la regla solo de los formatos seleccionados."
                  onClick={(event) => { event.currentTarget.closest('details').removeAttribute('open'); onUnlockSelected?.(); }}
                  className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <FiUnlock aria-hidden="true" />{lockSaving ? 'Actualizando…' : `Desbloquear seleccionados (${selectedLockedCount})`}
                </button>
              </div>
            </details>}
          </div>
        </div>
        {hiddenSelectedCount > 0 && <p className="mt-3 text-xs font-semibold text-amber-700">
          {hiddenSelectedCount} {hiddenSelectedCount === 1 ? 'seleccionado está' : 'seleccionados están'} fuera del filtro y también recibirá{hiddenSelectedCount === 1 ? '' : 'n'} los cambios.
        </p>}
      </div>

      <div className="min-w-0 border-t border-slate-200 pt-3 lg:border-t-0 lg:pt-0">
        <div className="flex items-center justify-between gap-2">
          <h2 id={`${generatedId}-title`} className="m-0 text-sm font-bold text-slate-900">{canEditPricing ? 'Editar selección' : 'Acciones de selección'}</h2>
          <button type="button" aria-label={`${mobileExpanded ? 'Ocultar' : 'Mostrar'} edición de selección`}
            aria-expanded={mobileExpanded} aria-controls={`${generatedId}-editor`} disabled={controlsDisabled}
            onClick={() => setMobileExpanded((value) => !value)}
            className="flex min-h-11 items-center gap-1 rounded-md px-2 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 lg:hidden">
            {mobileExpanded ? 'Ocultar' : 'Mostrar'}<FiChevronDown aria-hidden="true" className={`h-4 w-4 ${mobileExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <div id={`${generatedId}-editor`} className={mobileExpanded ? '' : 'hidden lg:block'}>
        {canEditPricing && <>
        <fieldset role="radiogroup" disabled={editingDisabled} className="mt-2 flex flex-wrap gap-2">
          <legend className="sr-only">Tipo de cambio para los formatos seleccionados</legend>
          {[
            ...(!rulesOnly ? [['Precio', 'Aplicar precio final', 'price']] : []),
            ['Regla', 'Aplicar regla de precio', 'rule'],
          ].map(([label, accessibleLabel, value]) => (
            <label key={value} className={toggleClassName(selectedOperation === value)}>
              <input type="radio" name={`${generatedId}-operation`} value={value} aria-label={accessibleLabel} checked={selectedOperation === value}
                onChange={() => { setOperation(value); clearError(); }}
                className="h-4 w-4 shrink-0 cursor-pointer border-slate-300 accent-blue-600 disabled:cursor-not-allowed" />
              {label}
            </label>
          ))}
        </fieldset>
        {rulesOnly && <p className="mt-2 text-xs text-slate-600">Oferta se usa para configurar la regla y ver el precio calculado. La disponibilidad y los precios temporales se gestionan desde Pack o Detalle.</p>}

        <div className="mt-2 grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
          <div className="grid min-w-0 gap-2">
            <fieldset hidden={applyRule}
              className="min-w-0"
              aria-invalid={errorField === 'operations' || errorField === 'sellingPrice'}
              aria-describedby={errorField === 'sellingPrice' || errorField === 'operations' ? errorId : undefined}>
              <legend className="sr-only">Precio final masivo</legend>
              <label htmlFor={`${generatedId}-price`} className="mb-1 block text-xs font-medium text-slate-600">Nuevo precio</label>
              <div className="relative">
                <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 flex w-8 items-center justify-center rounded-l-lg border-r border-slate-200 bg-slate-50 text-sm font-medium text-slate-600">$</span>
                <input id={`${generatedId}-price`} aria-label="Precio final masivo"
                  aria-invalid={errorField === 'sellingPrice'} aria-describedby={errorField === 'sellingPrice' ? errorId : undefined}
                  type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="off" value={sellingPrice}
                  disabled={editingDisabled || !applyPrice} placeholder="Ingresar monto"
                  onChange={(event) => { setSellingPrice(event.target.value); clearError(); }}
                  className={`${inputClassName} !pl-10`} />
              </div>
            </fieldset>

            <fieldset hidden={!applyRule} className="min-w-0" aria-invalid={errorField === 'operations' || errorField === 'ruleMode' || errorField === 'ruleValue'}
              aria-describedby={errorField.startsWith('rule') || errorField === 'operations' ? errorId : undefined}>
              <legend className="mb-1 text-sm font-medium text-slate-600">Regla de precio masiva</legend>
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,0.55fr)] gap-2">
                <select aria-label="Regla masiva de precio" aria-invalid={errorField === 'ruleMode'}
                  aria-describedby={errorField === 'ruleMode' ? errorId : undefined} value={ruleMode}
                  disabled={editingDisabled || !applyRule}
                  onChange={(event) => { setRuleMode(event.target.value); setRuleValue(''); clearError(); }}
                  className={inputClassName}>
                  <option value="">Elige una regla</option>
                  <option value="markup_amount">Sumar $</option><option value="markup_percent">Sumar %</option><option value="fixed_price">Precio fijo</option>
                  {complexPriceRules.length > 0 && <optgroup label="Reglas complejas">
                    {complexPriceRules.map((rule) => <option key={rule.id} value={`complex:${rule.id}`}>{rule.name}</option>)}
                  </optgroup>}
                  <option value="clear">Quitar regla</option>
                </select>
                {!complexRuleSelected && <div className="min-w-0">
                  <input aria-label="Valor de regla masiva" aria-invalid={errorField === 'ruleValue'}
                    aria-describedby={errorField === 'ruleValue' ? errorId : undefined}
                    type="text" inputMode={ruleMode === 'markup_percent' ? 'decimal' : 'numeric'} autoComplete="off" value={ruleValue}
                    disabled={editingDisabled || !applyRule || !ruleMode || ruleMode === 'clear'}
                    placeholder={ruleMode === 'markup_percent' ? '%' : '$'}
                    onChange={(event) => { setRuleValue(event.target.value); clearError(); }} className={inputClassName} />
                  {previewRule && <SharedRuleEquivalents formats={previewFormats} complexPriceRules={complexPriceRules} />}
                </div>}
              </div>
              {applyRule && complexRuleSelected && <p className="mt-1.5 text-xs font-semibold text-blue-700">Cada formato usará el rango que corresponda a su costo efectivo actual.</p>}
              {applyRule && monetaryRule && selectedCount > 0 && <p className={`mt-1.5 text-xs ${mixedPricingBases ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
                {mixedPricingBases ? 'Base mixta: el monto se aplicará por kg o por unidad según cada producto.'
                  : `El monto se aplicará ${selectedBasisSet.has('kg') ? 'por kg' : 'por unidad'}.`}
              </p>}
              {applyRule && !applyPrice && !rulesOnly && ruleMode && ruleMode !== 'clear' && selectedUnconfiguredCount > 0 && <p className="mt-1.5 text-xs font-semibold text-amber-700">
                Quedarán pendientes para completar: {selectedUnconfiguredCount} {selectedUnconfiguredCount === 1 ? 'formato no tiene precio configurado' : 'formatos no tienen precio configurado'}.
              </p>}
            </fieldset>

          </div>
          <button type="submit" aria-label="Aplicar cambios" disabled={editingDisabled}
            className="min-h-9 rounded-md bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? 'Aplicando…' : `Aplicar a ${selectedCount}`}
          </button>
        </div>
        </>}
        {!rulesOnly && <div className="mt-2 flex justify-end">
          <button type="button" aria-label="Crear precios temporales" disabled={editingDisabled || exceedsPromotionLimit}
            title={exceedsPromotionLimit ? `Selecciona hasta ${MAX_BULK_PROMOTION_COUNT} formatos para crear precios temporales.`
              : selectedUnconfiguredCount > 0 ? 'Los formatos sin precio quedarán pendientes para completarlos.'
              : 'Crea un precio temporal independiente para cada formato seleccionado.'}
            onClick={() => { if (exceedsPromotionLimit) return; clearError(); onCreatePromotions?.(); }}
            className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50">
            <FiTag aria-hidden="true" className="h-4 w-4" />Precio temporal
          </button>
        </div>}
        {canEditPricing && selectedLockedCount > 0 && <p className="mt-2 text-xs text-slate-600">
          Desbloquea la selección desde Bloqueos para editarla.
        </p>}
        {!rulesOnly && exceedsPromotionLimit && <p role="alert" className="mt-3 text-xs font-semibold text-red-700">
          Puedes crear precios temporales para un máximo de {MAX_BULK_PROMOTION_COUNT} formatos a la vez. Reduce la selección en {selectedCount - MAX_BULK_PROMOTION_COUNT}.
        </p>}
        {error && <p id={errorId} role="alert" className="mt-3 text-sm font-semibold text-red-600">{error}</p>}
        {disabled && !saving && <p className="mt-3 text-xs font-semibold text-amber-700" role="status">
          Espera a que terminen de guardarse los cambios individuales seleccionados.
        </p>}
        </div>
      </div>
    </form>
  );
};

export default QuickPriceBulkEditor;
